import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Target, Trophy, Check, XCircle } from 'lucide-react'
import { useTranslation } from '../hooks/use-i18n'
import { formatPoints } from '../utils/points-label'
import { getPublicPlayerPredictions } from '../services/publicService'
import type { LeaderboardEntry, PublicPlayerPredictionItem } from '../types/public'
import { getMatchDate, formatDate } from '../utils/dateHelper'
import MatchTeamsInline from './MatchTeamsInline'

interface ViewPublicPlayerStatsModalProps {
  entry: LeaderboardEntry
  tournamentIds: string[]
  viewType: 'stats' | 'games'
  isOpen: boolean
  onClose: () => void
}

function computePoints(item: PublicPlayerPredictionItem): { winner: number; score: number } {
  const { prediction, match } = item
  if (!match?.score || match.winner === undefined) return { winner: 0, score: 0 }
  const predictedWinner = prediction.winner ? String(prediction.winner).trim() : null
  const actualWinner = match.winner ? String(match.winner).trim() : null
  const winnerCorrect = predictedWinner === actualWinner
  let winnerPoint = 0
  if (winnerCorrect) {
    winnerPoint = match.winner ? 1 : 2
  }
  const predScoreStr = String(prediction.predictedScore || '').trim()
  const matchScoreStr = String(match.score || '').trim()
  let scorePoint = 0
  if (predScoreStr && matchScoreStr && predScoreStr === matchScoreStr && winnerCorrect) {
    const [a, b] = matchScoreStr.split('-').map((s) => parseInt(s.trim() || '0', 10))
    if (a >= 5 || b >= 5) {
      scorePoint = a === b ? 5 : 5
    } else {
      const rules: Record<string, number> = {
        '1-0': 1, '2-0': 1, '2-1': 1, '0-0': 2, '1-1': 2, '2-2': 2,
        '3-0': 2, '3-1': 2, '3-2': 2, '3-3': 3, '4-0': 3, '4-1': 3, '4-2': 3, '4-3': 3, '4-4': 4,
      }
      scorePoint = rules[predScoreStr] ?? 1
    }
  }
  return { winner: winnerPoint, score: scorePoint }
}

const ViewPublicPlayerStatsModal: React.FC<ViewPublicPlayerStatsModalProps> = ({
  entry,
  tournamentIds,
  viewType,
  isOpen,
  onClose,
}) => {
  const { t } = useTranslation()
  const idsKey = [...tournamentIds].sort().join(',')

  const { data: items = [], isLoading: loading } = useQuery({
    queryKey: ['public-player-predictions', entry.userId, idsKey],
    queryFn: () => getPublicPlayerPredictions(entry.userId, tournamentIds),
    enabled: isOpen && !!entry?.userId && tournamentIds.length > 0,
    staleTime: 60 * 1000,
  })

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const dateA = new Date(getMatchDate(a.match, true)).getTime()
      const dateB = new Date(getMatchDate(b.match, true)).getTime()
      return dateB - dateA
    })
  }, [items])

  const points = useMemo(() => {
    let totalPoints = 0
    let totalPointScores = 0
    let totalPointVictories = 0
    let totalPointDraw = 0
    const withResult = items.filter((i) => i.match?.score && i.match.winner !== undefined)
    for (const item of withResult) {
      const { winner: w, score: s } = computePoints(item)
      totalPoints += w + s
      totalPointVictories += w
      totalPointScores += s
      if (!item.match.winner) totalPointDraw += w
    }
    return { totalPoints, totalPointScores, totalPointVictories, totalPointDraw }
  }, [items])

  const correctWinners = useMemo(
    () => items.filter((p) => p.match?.score && p.match.winner !== undefined && (p.prediction.winner ?? '').trim() === (p.match.winner ?? '').trim()).length,
    [items]
  )
  const correctScores = useMemo(
    () => items.filter((p) => p.match?.score && p.prediction.predictedScore && String(p.match.score).trim() === String(p.prediction.predictedScore).trim()).length,
    [items]
  )
  const drawsCorrect = useMemo(
    () => items.filter((p) => p.match?.score && !p.match.winner && (p.prediction.winner === null || p.prediction.winner === '' || p.prediction.winner === 'null')).length,
    [items]
  )

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={handleBackdropClick}
    >
      <div
        className="w-full max-w-2xl mx-2 sm:mx-4 glass-strong rounded-xl sm:rounded-2xl p-3 sm:p-5 lg:p-6 max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-2 mb-4">
          <div className="min-w-0 flex-1">
            <h2 className="text-lg sm:text-xl font-display font-bold text-foreground truncate">{entry.userName}</h2>
            <p className="text-sm text-muted-foreground">{entry.countryName}{entry.city ? ` · ${entry.city}` : ''}</p>
            <p className="text-sm text-muted-foreground mt-1">{formatPoints(entry.totalPoints, t)} · {entry.predictionCount} {t('publicLeaderboard.games')}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 shrink-0" aria-label="Close">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {viewType === 'games' ? (
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
                  <p className="text-muted-foreground">{t('stats.loadingPredictions')}</p>
                </div>
              ) : sortedItems.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">{t('stats.didNotPlayYet')}</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[50vh] overflow-y-auto">
                  {sortedItems.map((item) => {
                    const { winner: wPt, score: sPt } = computePoints(item)
                    const totalPt = wPt + sPt
                    const hasResult = !!item.match?.score && item.match.winner !== undefined
                    const hasPoints = hasResult && totalPt > 0
                    const zeroPoints = hasResult && totalPt === 0
                    return (
                      <div key={item.prediction.id} className="glass-card rounded-xl p-3 sm:p-4 border border-white/10 shadow-sm">
                        <div className="flex justify-between items-start gap-2 mb-1.5">
                          <MatchTeamsInline
                            teamA={item.match.teamA}
                            teamB={item.match.teamB}
                            league={item.match.league}
                            className="font-semibold text-foreground text-sm sm:text-base break-words"
                            nameClassName="font-semibold text-foreground text-sm sm:text-base"
                          />
                          {hasResult && (
                            <span className={`flex items-center gap-1.5 text-sm font-semibold shrink-0 ${hasPoints ? 'text-emerald-500' : zeroPoints ? 'text-red-500' : 'text-foreground'}`}>
                              {hasPoints ? <Check className="w-4 h-4" /> : zeroPoints ? <XCircle className="w-4 h-4" /> : null}
                              +{formatPoints(totalPt, t)}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mb-2">{formatDate(getMatchDate(item.match), true)}</div>
                        <div className="flex items-start gap-2 text-sm mb-1">
                          <Target className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
                          <span className="text-foreground">
                            {t('stats.playerScoreLabel')} {item.prediction.predictedScore}
                            {item.prediction.winner ? ` (${item.prediction.winner})` : ` (${t('stats.draw')})`}
                          </span>
                        </div>
                        {hasResult && (
                          <div className="flex items-start gap-2 text-sm text-muted-foreground mt-2">
                            <Trophy className="w-4 h-4 shrink-0 mt-0.5 opacity-80" />
                            <span>
                              {t('stats.resultLabel')} {item.match.score}
                              {item.match.winner ? ` (${item.match.winner})` : ` (${t('stats.draw')})`}
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </>
          ) : (
            <>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-3" />
                  <p className="text-muted-foreground">{t('stats.loadingStats')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="glass-card rounded-lg p-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.totalGames')}</span>
                        <span className="font-semibold">{items.length}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.successRatio')}</span>
                        <span className="font-semibold">
                          {items.length ? Math.round((correctWinners / items.length) * 100) : 0}%
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.points')}</span>
                        <span className="font-semibold">{points.totalPoints}</span>
                      </div>
                    </div>
                  </div>
                  <div className="glass-card rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">🏆</span>
                      <span className="font-semibold">{t('stats.winners')}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.winnersCount')}</span>
                        <span className="font-semibold">{correctWinners}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.points')}</span>
                        <span className="font-semibold">{points.totalPointVictories}</span>
                      </div>
                    </div>
                  </div>
                  <div className="glass-card rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">⚖️</span>
                      <span className="font-semibold">{t('stats.draws')}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.drawsCount')}</span>
                        <span className="font-semibold">{drawsCorrect}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.points')}</span>
                        <span className="font-semibold">{points.totalPointDraw}</span>
                      </div>
                    </div>
                  </div>
                  <div className="glass-card rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xl">⚽</span>
                      <span className="font-semibold">{t('stats.scores')}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.scoresCount')}</span>
                        <span className="font-semibold">{correctScores}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{t('stats.points')}</span>
                        <span className="font-semibold">{points.totalPointScores}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-muted/30">
          <button
            onClick={onClose}
            className="w-full px-4 py-3 rounded-xl font-semibold bg-primary text-primary-foreground hover:opacity-90"
          >
            {t('stats.close')}
          </button>
        </div>
      </div>
    </div>
  )
}

export default ViewPublicPlayerStatsModal
